import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  priceConfigsApi,
  priceImportsApi,
  sellersApi,
  type PriceConfig,
  type PriceConfigInput,
  type PreviewResponse,
  type SellerListItem,
  KNOWN_PRICE_FIELDS,
  type KnownPriceField,
  ApiError,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { useBasePath, withBase } from '../lib/basePath';

const FIELD_LABELS: Record<KnownPriceField, string> = {
  external_offer_id: 'Артикул (offerId) — обязательно',
  name:              'Название — обязательно',
  description:       'Описание',
  price_rub:         'Цена, ₽',
  qty:               'Количество (qty)',
  category_id:       'categoryId Flowwow',
  sub_category_id:   'subCategoryId',
  height:            'Высота, см',
  width:             'Ширина, см',
  country:           'Страна (regionOfOrigin)',
  manufacturer:      'Производитель (manufacturerName)',
  stem_length:       'Длина стебля',
  flower_type:       'Вид цветка',
  composition:       'Состав / материал',
  photo_url:         'URL фото (если в Excel ссылкой)',
  consist_name:      'Состав: название',
  consist_qty:       'Состав: количество',
  consist_unit:      'Состав: ед. изм.',
};

export default function PriceConfigEdit() {
  const { id: idStr } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const base = useBasePath();
  const isNew = !idStr || idStr === 'new';
  const id = isNew ? null : Number(idStr);

  const [sellers, setSellers] = useState<SellerListItem[]>([]);
  const [sellerId, setSellerId] = useState<number | null>(user?.role === 'admin' ? null : (user?.id ?? null));
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(1);
  const [dataStartRow, setDataStartRow] = useState(2);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [photoSource, setPhotoSource] = useState<'none' | 'embedded' | 'url_column'>('none');
  const [photoColumn, setPhotoColumn] = useState('');
  const [defaultsRows, setDefaultsRows] = useState<{ key: string; value: string }[]>([]);
  const [notes, setNotes] = useState('');

  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  // Test-parse state
  const [testPreview, setTestPreview] = useState<PreviewResponse | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user?.role === 'admin') sellersApi.list().then((r) => setSellers(r.sellers)).catch(() => { /* noop */ });
  }, [user]);

  useEffect(() => {
    if (isNew || !id) return;
    priceConfigsApi.get(id)
      .then(({ config: c }) => {
        setSellerId(c.seller_id);
        setName(c.name);
        setIsDefault(c.is_default);
        setSheetIndex(c.sheet_index);
        setHeaderRow(c.header_row);
        setDataStartRow(c.data_start_row);
        setColumnMap(c.column_map);
        setPhotoSource(c.photo_source);
        setPhotoColumn(c.photo_column ?? '');
        setDefaultsRows(Object.entries(c.defaults).map(([k, v]) => ({ key: k, value: String(v) })));
        setNotes(c.notes ?? '');
      })
      .catch((e) => setErr(String(e?.message ?? e)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  function inputObject(): PriceConfigInput {
    const defaults: Record<string, string | number> = {};
    for (const r of defaultsRows) {
      const k = r.key.trim();
      if (!k) continue;
      const num = Number(r.value);
      defaults[k] = !Number.isNaN(num) && r.value.trim() !== '' ? num : r.value;
    }
    return {
      name: name.trim(),
      is_default: isDefault,
      sheet_index: sheetIndex,
      header_row: headerRow,
      data_start_row: dataStartRow,
      column_map: Object.fromEntries(
        Object.entries(columnMap).filter(([, v]) => v && v.trim().length > 0),
      ),
      package_size_parser: null,
      photo_source: photoSource,
      photo_column: photoSource === 'url_column' ? (photoColumn.trim() || null) : null,
      defaults,
      notes: notes.trim() || null,
    };
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setSavedMsg(null);
    try {
      if (isNew) {
        if (!sellerId) throw new Error('Не выбран продавец');
        const r = await priceConfigsApi.create({ ...inputObject(), seller_id: sellerId });
        nav(withBase(base, `/prices/configs/${r.config.id}`), { replace: true });
      } else if (id) {
        await priceConfigsApi.update(id, inputObject());
        setSavedMsg('Сохранено');
      }
    } catch (e) {
      if (e instanceof ApiError && e.body && typeof e.body === 'object' && 'field' in e.body) {
        setErr(`Неизвестное поле в маппинге: ${(e.body as { field: string }).field}`);
      } else {
        setErr(String((e as Error)?.message ?? e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!id) return;
    if (!confirm('Удалить шаблон?')) return;
    await priceConfigsApi.remove(id);
    nav(withBase(base, '/prices/configs'), { replace: true });
  }

  async function onTestParse() {
    setPreviewErr(null);
    setTestPreview(null);
    const f = fileRef.current?.files?.[0];
    if (!f) { setPreviewErr('Выбери .xlsx'); return; }
    if (isNew && !id) { setPreviewErr('Сначала сохрани шаблон, потом тестируй парсинг'); return; }
    setPreviewing(true);
    try {
      const r = await priceImportsApi.preview(f, id!, 10);
      setTestPreview(r);
    } catch (e) {
      setPreviewErr(String((e as Error)?.message ?? e));
    } finally {
      setPreviewing(false);
    }
  }

  function setMap(field: KnownPriceField, value: string) {
    setColumnMap({ ...columnMap, [field]: value });
  }

  function addDefaultRow() { setDefaultsRows([...defaultsRows, { key: '', value: '' }]); }
  function updateDefaultRow(idx: number, patch: Partial<{ key: string; value: string }>) {
    setDefaultsRows(defaultsRows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeDefaultRow(idx: number) {
    setDefaultsRows(defaultsRows.filter((_, i) => i !== idx));
  }

  if (loading) return <div className="container"><p className="muted">Загрузка…</p></div>;

  return (
    <div className="container">
      <div className="page-head">
        <h2>{isNew ? 'Новый шаблон прайса' : `Шаблон #${id}`}</h2>
        <div className="row">
          <Link to={withBase(base, '/prices/configs')} className="btn">← К шаблонам</Link>
          {!isNew && <button className="btn" onClick={onDelete} type="button">Удалить</button>}
        </div>
      </div>

      <form className="card form" onSubmit={onSubmit}>
        {isNew && user?.role === 'admin' && (
          <label>
            <span>Продавец *</span>
            <select value={sellerId ?? ''} onChange={(e) => setSellerId(Number(e.target.value) || null)} required>
              <option value="">— выберите —</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.login}</option>)}
            </select>
          </label>
        )}

        <label>
          <span>Название шаблона *</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="например: Прайс роз Florentika" required />
        </label>

        <div className="row" style={{ gap: 12 }}>
          <label style={{ flex: 1 }}>
            <span>Индекс листа</span>
            <input type="number" min={0} value={sheetIndex} onChange={(e) => setSheetIndex(Number(e.target.value))} />
          </label>
          <label style={{ flex: 1 }}>
            <span>Строка с заголовками</span>
            <input type="number" min={1} value={headerRow} onChange={(e) => setHeaderRow(Number(e.target.value))} />
          </label>
          <label style={{ flex: 1 }}>
            <span>Первая строка данных</span>
            <input type="number" min={1} value={dataStartRow} onChange={(e) => setDataStartRow(Number(e.target.value))} />
          </label>
        </div>

        <label className="row">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          <span>Шаблон по умолчанию для продавца</span>
        </label>

        <fieldset className="fieldset">
          <legend>Маппинг колонок</legend>
          <p className="muted" style={{ marginTop: 0 }}>
            Для каждого поля укажи либо букву колонки (A, B, …), либо точный текст заголовка из шапки.
          </p>
          <div className="map-grid">
            {KNOWN_PRICE_FIELDS.map((field) => (
              <div key={field} className="map-row">
                <span className="map-field">{FIELD_LABELS[field]}</span>
                <input
                  value={columnMap[field] ?? ''}
                  onChange={(e) => setMap(field, e.target.value)}
                  placeholder="A или 'Артикул'"
                />
              </div>
            ))}
          </div>
        </fieldset>

        <fieldset className="fieldset">
          <legend>Фото</legend>
          <label>
            <span>Источник фото</span>
            <select value={photoSource} onChange={(e) => setPhotoSource(e.target.value as 'none' | 'embedded' | 'url_column')}>
              <option value="none">Нет / добавляем вручную</option>
              <option value="url_column">URL в колонке Excel</option>
              <option value="embedded" disabled>Вставлено в Excel (на следующем этапе)</option>
            </select>
          </label>
          {photoSource === 'url_column' && (
            <label>
              <span>Колонка с URL (A / B / заголовок)</span>
              <input value={photoColumn} onChange={(e) => setPhotoColumn(e.target.value)} placeholder="например: F" />
            </label>
          )}
        </fieldset>

        <fieldset className="fieldset">
          <legend>Значения по умолчанию (если в Excel пусто)</legend>
          {defaultsRows.length === 0 && <p className="muted">Например: category_id = 450, country = Россия.</p>}
          {defaultsRows.map((r, i) => (
            <div className="row" key={i} style={{ gap: 8 }}>
              <input style={{ flex: 1 }} value={r.key} onChange={(e) => updateDefaultRow(i, { key: e.target.value })} placeholder="имя поля (например, category_id)" />
              <input style={{ flex: 2 }} value={r.value} onChange={(e) => updateDefaultRow(i, { value: e.target.value })} placeholder="значение" />
              <button type="button" className="btn" onClick={() => removeDefaultRow(i)}>✕</button>
            </div>
          ))}
          <button type="button" className="btn" onClick={addDefaultRow}>+ default</button>
        </fieldset>

        <label>
          <span>Заметки</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        {err && <div className="error">{err}</div>}

        <div className="row">
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? 'Сохраняем…' : (isNew ? 'Создать' : 'Сохранить')}
          </button>
          {savedMsg && <span className="ok-msg">{savedMsg}</span>}
        </div>
      </form>

      {!isNew && (
        <div className="card form" style={{ marginTop: 20 }}>
          <h3>Тестовый парсинг</h3>
          <p className="muted">Залей пример .xlsx — увидишь, как маппинг применился к первым 10 строкам.</p>
          <div className="row">
            <input ref={fileRef} type="file" accept=".xlsx" />
            <button type="button" className="btn" onClick={onTestParse} disabled={previewing}>
              {previewing ? 'Парсим…' : 'Тестовый парсинг'}
            </button>
          </div>
          {previewErr && <div className="error" style={{ marginTop: 10 }}>{previewErr}</div>}

          {testPreview && (
            <div style={{ marginTop: 14 }}>
              <p className="muted">
                Лист: <code>{testPreview.sheetName}</code>, заголовок: строка {testPreview.headerRow},
                всего строк данных: {testPreview.total_rows}.
              </p>
              <p>
                <strong>Распознанные колонки шапки:</strong>{' '}
                {Object.entries(testPreview.headers).map(([c, txt]) => (
                  <span key={c} className="badge ok" style={{ marginRight: 6 }}>{c}: {txt}</span>
                ))}
              </p>
              <p>
                <strong>Маппинг применился:</strong>{' '}
                {Object.entries(testPreview.colMap).map(([f, c]) => (
                  <span key={f} className="badge ok" style={{ marginRight: 6 }}>{f} ← col {c}</span>
                ))}
              </p>
              {testPreview.preview.length === 0 ? (
                <p className="muted">Парсер не нашёл строк данных.</p>
              ) : (
                <table className="grid compact">
                  <thead>
                    <tr>
                      <th>#</th>
                      {KNOWN_PRICE_FIELDS.filter((f) => testPreview.preview.some((r) => r.mapped[f] != null)).map((f) => (
                        <th key={f}>{f}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {testPreview.preview.map((r) => (
                      <tr key={r.rowNum}>
                        <td>{r.rowNum}</td>
                        {KNOWN_PRICE_FIELDS.filter((f) => testPreview.preview.some((x) => x.mapped[f] != null)).map((f) => (
                          <td key={f}>{r.mapped[f] != null ? String(r.mapped[f]) : <span className="muted">—</span>}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
